import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Center,
  Field,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Switch } from '@chakra-ui/react';
import {
  DataTable,
  Button as ButtonComponent,
} from '@themes/aegix';
import { SelectInput } from '@themes/aegix';
import { DrawerComponent } from '../ui/drawerComponent';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import type {
  WatchListEntryWrite,
  FlagType,
  SeverityLevel,
} from '../../types/VisitorManagementTypes';
import UploadComponent from '../file-upload';
import { useWatchListCrud } from './hooks/useWatchListCrud';
import { useVisitorPermissions } from '../../hooks/useVisitorPermissions';

export function WatchListContent() {
  const { t } = useTranslation();

  const FLAG_TYPE_OPTIONS = useMemo(() => [
    { label: t('visitors.watchlistType.sexOffender', 'Sex Offender'), value: 'sex_offender' },
    { label: t('visitors.tab.watchlist', 'Watch List'), value: 'watch_list' },
    { label: t('visitors.watchlistType.orderOfProtection', 'Order of Protection'), value: 'order_of_protection' },
    { label: t('visitors.watchlistType.custodyRestriction', 'Custody Restriction'), value: 'custody_restriction' },
    { label: t('visitors.watchlistType.custom', 'Custom'), value: 'custom' },
  ], [t]);

  const SEVERITY_OPTIONS = useMemo(() => [
    { label: t('visitors.severity.low', 'Low'), value: 'low' },
    { label: t('visitors.severity.medium', 'Medium'), value: 'medium' },
    { label: t('visitors.severity.high', 'High'), value: 'high' },
    { label: t('visitors.severity.critical', 'Critical'), value: 'critical' },
  ], [t]);

  const { canManage, isLoading: permissionsLoading } = useVisitorPermissions();
  const {
    isLoading,
    error,
    drawerOpen,
    setDrawerOpen,
    editItem,
    handleClose,
    handleSubmit,
    handleDelete,
    handleRowClick,
    initialValues,
    rows,
    columns,
  } = useWatchListCrud();

  const methods = useForm<WatchListEntryWrite>({
    defaultValues: initialValues,
    mode: 'onBlur',
  });

  const { register, control, watch, setValue, formState: { errors } } = methods;

  useEffect(() => {
    methods.reset(initialValues);
  }, [initialValues]);

  const onSubmit = (values: WatchListEntryWrite) => {
    handleSubmit(values, methods.setError);
  };

  const values = watch();

  if (!permissionsLoading && !canManage) {
    return (
      <Center py={12}>
        <Text color="text.secondary" data-e2e="watchlist-access-denied">
          You do not have permission to access Watch List.
        </Text>
      </Center>
    );
  }

  if (permissionsLoading || isLoading) {
    return (
      <Center py={12}>
        <Spinner size="lg" color="button.primary" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center py={12}>
        <Text color="text.danger">
          {error instanceof Error ? error.message : 'Failed to load data'}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <Box px={5} pb={2} pt={4} display="flex" justifyContent="flex-end">
        <ButtonComponent visual="primary" onClick={() => setDrawerOpen(true)} data-e2e="add-watch-list-entry-button">
          {t('visitors.addWatchListEntry', 'Add Watch List Entry')}
        </ButtonComponent>
      </Box>
      <Box px={5} pb={5}>
        {rows.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('visitors.noWatchListEntries', 'No watch list entries')}</Text>
          </Center>
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            keyExtractor={(row) => row.id}
            onRowClick={handleRowClick}
          />
        )}
      </Box>

      <DrawerComponent
        isOpen={drawerOpen}
        onClose={handleClose}
        title={editItem ? t('visitors.editWatchListEntry', 'Edit Watch List Entry') : t('visitors.addWatchListEntry', 'Add Watch List Entry')}
        size="md"
        footer={
          <SimpleGrid columns={editItem ? 3 : 2} gap={4} width="100%">
            {editItem && (
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={handleDelete}
               data-e2e="delete-button">
                {t('common.delete', 'Delete')}
              </ButtonComponent>
            )}
            <ButtonComponent
              visual="secondary"
              type="button"
              onClick={handleClose}
             data-e2e="cancel-button">
              {t('common.cancel', 'Cancel')}
            </ButtonComponent>
            <ButtonComponent
              visual="primary"
              type="submit"
              form="watchlist-form"
             data-e2e="save-button">
              {t('common.save', 'Save')}
            </ButtonComponent>
          </SimpleGrid>
        }
      >
        <FormProvider {...methods}>
          <form id="watchlist-form" onSubmit={methods.handleSubmit(onSubmit)}>
            <VStack gap={4} align="stretch">
              <SimpleGrid columns={2} gap={4}>
                <Field.Root invalid={!!errors.first_name}>
                  <Field.Label>{t('common.firstName', 'First Name')}</Field.Label>
                  <Input {...register('first_name')} />
                  {errors.first_name && (
                    <Field.ErrorText data-e2e="watchlist-firstname-error">{errors.first_name.message ? t(errors.first_name.message) : null}</Field.ErrorText>
                  )}
                </Field.Root>
                <Field.Root invalid={!!errors.last_name}>
                  <Field.Label>{t('common.lastName', 'Last Name')}</Field.Label>
                  <Input {...register('last_name')} />
                  {errors.last_name && (
                    <Field.ErrorText data-e2e="watchlist-lastname-error">{errors.last_name.message ? t(errors.last_name.message) : null}</Field.ErrorText>
                  )}
                </Field.Root>
              </SimpleGrid>
              <Field.Root>
                <Field.Label>{t('visitors.dateOfBirth', 'Date of Birth')}</Field.Label>
                <Input
                  {...register('date_of_birth')}
                  type="date"
                />
              </Field.Root>
              <Controller
                name="flag_type"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    label={t('visitors.col.flagType', 'Flag Type')}
                    name="flag_type"
                    value={field.value}
                    onChange={(val: string) => field.onChange(val as FlagType)}
                    option={FLAG_TYPE_OPTIONS}
                    e2eTitle="watchlist-flag-type-select"
                  />
                )}
              />
              <Controller
                name="severity"
                control={control}
                render={({ field }) => (
                  <SelectInput
                    label={t('visitors.col.severity', 'Severity')}
                    name="severity"
                    value={field.value}
                    onChange={(val: string) => field.onChange(val as SeverityLevel)}
                    option={SEVERITY_OPTIONS}
                    e2eTitle="watchlist-severity-select"
                  />
                )}
              />
              <Field.Root invalid={!!errors.reason}>
                <Field.Label>{t('visitors.col.reason', 'Reason')}</Field.Label>
                <Textarea {...register('reason')}
                  rows={3}
                   data-e2e="watchlist-reason-input" />
                {errors.reason && (
                  <Field.ErrorText data-e2e="watchlist-reason-error">{errors.reason.message ? t(errors.reason.message) : null}</Field.ErrorText>
                )}
              </Field.Root>
              <Field.Root>
                <Field.Label>{t('visitors.documentation', 'Documentation')}</Field.Label>
                <UploadComponent
                  name="documentation"
                  values={values as unknown as Record<string, unknown>}
                  setFieldValue={setValue as (field: string, value: unknown) => void}
                />
              </Field.Root>
              <SimpleGrid columns={2} gap={4}>
                <Field.Root>
                  <Field.Label>{t('visitors.effectiveFrom', 'Effective From')}</Field.Label>
                  <Input
                    {...register('effective_from')}
                    type="date"
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label>{t('visitors.effectiveUntil', 'Effective Until')}</Field.Label>
                  <Input
                    {...register('effective_until')}
                    type="date"
                  />
                </Field.Root>
              </SimpleGrid>
              <Field.Root>
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch.Root
                      checked={field.value}
                      onCheckedChange={(d) => field.onChange(d.checked)}
                    >
                      <Switch.HiddenInput data-e2e="watchlist-active-switch" />
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                      <Switch.Label>{t('common.active', 'Active')}</Switch.Label>
                    </Switch.Root>
                  )}
                />
              </Field.Root>
            </VStack>
          </form>
        </FormProvider>
      </DrawerComponent>
    </Box>
  );
}
